import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  ChevronDown
} from 'lucide-react';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [expandedSubmenu, setExpandedSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: 'À propos', href: '/about' },
    { name: 'Services', href: '/services', hasDropdown: true },
    { name: 'Produits', href: '/products', hasDropdown: true },
  ];

  // Nouvelle structure des services selon la demande
  const servicesLinks = [
    { 
      category: 'Services',
      links: [
        { 
          name: 'Logistique', 
          href: '/services#logistique',
          subItems: [
            { name: 'Transport', href: '/services#transport' },
            { name: 'Douanes', href: '/services#douanes' },
            { name: 'Stockage', href: '/services#stockage' }
          ]
        },
        { name: 'Hydrocarbures & BTP', href: '/services#bases' },
        { name: 'Produits Alimentaires', href: '/services#alimentaires' },
        { name: 'Consultance', href: '/services#conseil' }
      ]
    }
  ];

  // Nouvelle structure des produits
  const productsLinks = [
    { 
      category: 'Produits',
      links: [
        { 
          name: 'Hydrocarbures & BTP',
          href: '/products#bases',
          subItems: [
            { name: 'Hydrocarbures', href: '/products#petrole' },
            { name: 'BTP & Matériaux', href: '/products#btp' }
          ]
        },
        { 
          name: 'Produits Alimentaires',
          href: '/products#alimentaires',
          subItems: [
            { name: 'Denrées Première Nécessité', href: '/products#denrees' },
            { name: 'Produits Halieutiques', href: '/products#halieutiques' }
          ]
        }
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  // Fonction pour gérer la navigation vers une section spécifique
  const handleSectionNavigation = (href, submenu = null) => {
    // Fermer le menu mobile si ouvert
    setIsOpen(false);
    
    // Fermer tous les dropdowns
    setActiveDropdown(null);
    setExpandedSubmenu(null);
    
    // Naviguer vers la page
    navigate(href);
    
    // Le scroll vers la section sera géré par la page destination
  };

  // Fonction pour naviguer vers la page principale (Services ou Produits)
  const handleMainNavigation = (href) => {
    setIsOpen(false);
    setActiveDropdown(null);
    setExpandedSubmenu(null);
    navigate(href);
  };

  // Fonction pour toggle le dropdown desktop
  const toggleDropdown = (dropdownName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (activeDropdown === dropdownName) {
      setActiveDropdown(null);
      setExpandedSubmenu(null);
    } else {
      setActiveDropdown(dropdownName);
      setExpandedSubmenu(null);
    }
  };

  // Fonction pour toggle le sous-menu
  const toggleSubmenu = (submenuName, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (expandedSubmenu === submenuName) {
      setExpandedSubmenu(null);
    } else {
      setExpandedSubmenu(submenuName);
    }
  };

  // Fermer les dropdowns quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
        setExpandedSubmenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled
        ? 'bg-white/95 backdrop-blur-md shadow-medium'
        : 'bg-transparent'
    }`}>
      {/* Top Bar - Visible aussi en mobile */}
      <div className={`transition-all duration-300 ${
        isScrolled ? 'bg-gray-100' : 'bg-[#4B9D96]'
      }`}>
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3 md:space-x-6 text-black">
              <div className="hidden sm:flex items-center space-x-2">
                <Phone className="w-3 h-3 md:w-4 md:h-4" />
                <span className="text-xs md:text-sm">+221 33 802 84 84</span>
              </div>
              <div className="sm:flex items-center space-x-2">
                <a 
                  href="mailto:lst@lstoil.com"
                  className="flex items-center space-x-2 hover:text-[#cd3127] hover:bg-[#4B9D96]/10 px-2 py-1 rounded-md transition-all duration-300 cursor-pointer group"
                >
                  <Mail className="w-4 h-4 group-hover:animate-pulse" />
                  <span>lst@lstoil.com</span>
                </a>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-xs md:text-sm">Dakar, Sénégal</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md' : 'bg-[#4B9D96]'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img
                src={isScrolled ? "/images/logolstoil.png" : "/images/logorouge.png"}
                alt="LST OIL SA"
                className="w-32 h-10 rounded-lg transition-all duration-300"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigation.map((item) => (
                <div key={item.name} className="relative dropdown-container">
                  {item.hasDropdown ? (
                    <div className="relative">
                      <div className="flex items-center space-x-1">
                        {/* Lien principal cliquable */}
                        <Link
                          to={item.href}
                          className={`transition-colors duration-300 ${
                            isScrolled
                              ? 'text-black hover:text-[#4B9D96]'
                              : 'text-white hover:text-black'
                          } ${isActive(item.href) ? 'font-semibold' : ''}`}
                          onClick={() => handleMainNavigation(item.href)}
                        >
                          {item.name}
                        </Link>
                        {/* Bouton dropdown */}
                        <button
                          onClick={(e) => toggleDropdown(item.name, e)}
                          className={`transition-colors duration-300 ${
                            isScrolled ? 'text-black hover:text-[#4B9D96]' : 'text-white hover:text-black'
                          } ${activeDropdown === item.name ? 'text-[#4B9D96]' : ''}`}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${
                            activeDropdown === item.name ? 'rotate-180' : ''
                          }`} />
                        </button>
                      </div>
                      
                      {/* Dropdown Services */}
                      {item.name === 'Services' && activeDropdown === 'Services' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
                        >
                          <div className="w-80 bg-white rounded-xl shadow-strong border border-gray-100 py-4">
                            {servicesLinks.map((section) => (
                              <div key={section.category} className="px-4">
                                <div className="px-2 pb-3 text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100">
                                  {section.category}
                                </div>
                                <div className="flex flex-col mt-2">
                                  {section.links.map((link) => (
                                    <div key={link.name} className="relative">
                                      {link.subItems ? (
                                        <>
                                          <button
                                            onClick={(e) => toggleSubmenu(link.name, e)}
                                            className="flex items-center justify-between w-full px-2 py-3 rounded-md text-gray-700 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm font-medium cursor-pointer"
                                          >
                                            <span>{link.name}</span>
                                            <ChevronDown className={`w-4 h-4 transform transition-transform ${
                                              expandedSubmenu === link.name ? 'rotate-180' : ''
                                            }`} />
                                          </button>
                                          <AnimatePresence>
                                            {expandedSubmenu === link.name && (
                                              <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="ml-4 pl-2 border-l-2 border-gray-100 overflow-hidden"
                                              >
                                                {link.subItems.map((subItem) => (
                                                  <button
                                                    key={subItem.name}
                                                    onClick={() => handleSectionNavigation(subItem.href)}
                                                    className="block w-full text-left px-2 py-2 rounded-md text-gray-600 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm"
                                                  >
                                                    {subItem.name}
                                                  </button>
                                                ))}
                                              </motion.div>
                                            )}
                                          </AnimatePresence>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => handleSectionNavigation(link.href)}
                                          className="w-full text-left px-2 py-3 rounded-md text-gray-700 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm font-medium"
                                        >
                                          {link.name}
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* Dropdown Produits */}
                      {item.name === 'Produits' && activeDropdown === 'Produits' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
                        >
                          <div className="w-80 bg-white rounded-xl shadow-strong border border-gray-100 py-4">
                            {productsLinks.map((section) => (
                              <div key={section.category} className="px-4">
                                <div className="px-2 pb-3 text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-100">
                                  {section.category}
                                </div>
                                <div className="flex flex-col mt-2">
                                  {section.links.map((link) => (
                                    <div key={link.name} className="relative">
                                      {link.subItems ? (
                                        <>
                                          <button
                                            onClick={(e) => toggleSubmenu(link.name, e)}
                                            className="flex items-center justify-between w-full px-2 py-3 rounded-md text-gray-700 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm font-medium cursor-pointer"
                                          >
                                            <span>{link.name}</span>
                                            <ChevronDown className={`w-4 h-4 transform transition-transform ${
                                              expandedSubmenu === link.name ? 'rotate-180' : ''
                                            }`} />
                                          </button>
                                          <AnimatePresence>
                                            {expandedSubmenu === link.name && (
                                              <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="ml-4 pl-2 border-l-2 border-gray-100 overflow-hidden"
                                              >
                                                {link.subItems.map((subItem) => (
                                                  <button
                                                    key={subItem.name}
                                                    onClick={() => handleSectionNavigation(subItem.href)}
                                                    className="block w-full text-left px-2 py-2 rounded-md text-gray-600 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm"
                                                  >
                                                    {subItem.name}
                                                  </button>
                                                ))}
                                              </motion.div>
                                            )}
                                          </AnimatePresence>
                                        </>
                                      ) : (
                                        <button
                                          onClick={() => handleSectionNavigation(link.href)}
                                          className="w-full text-left px-2 py-3 rounded-md text-gray-700 hover:text-[#6bb8b1] hover:bg-blue-50 transition-colors duration-200 text-sm font-medium"
                                        >
                                          {link.name}
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      className={`transition-colors duration-300 ${
                        isScrolled
                          ? 'text-black hover:text-[#4B9D96]'
                          : 'text-white hover:text-[#000000]'
                      } ${isActive(item.href) ? 'font-semibold' : ''}`}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Bouton Contact avec fond vert et rouge après scroll */}
              <Link
                to="/contact"
                className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 ${
                  isScrolled
                    ? 'bg-gradient-to-r from-[#4B9D96] to-[#cd3127] text-white hover:shadow-lg'
                    : 'bg-[#cd3127] text-white hover:bg-[#cd3127]/90'
                }`}
              >
                Contact
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors duration-300 ${
                isScrolled ? 'text-gray-700 hover:text-[#4b9d96]' : 'text-white hover:text-black'
              }`}
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-200 shadow-strong max-h-[80vh] overflow-y-auto"
          >
            <div className="container mx-auto px-4 py-6">
              <div className="space-y-4">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.hasDropdown ? (
                      <div className="space-y-3">
                        {/* Services Mobile */}
                        {item.name === 'Services' && (
                          <div>
                            <div className="flex items-center justify-between">
                              <Link
                                to={item.href}
                                className="text-lg font-semibold text-gray-900 hover:text-[#4b9d96] transition-colors"
                                onClick={() => setIsOpen(false)}
                              >
                                Services
                              </Link>
                              <button
                                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                                className="p-2 text-gray-500 hover:text-[#4b9d96]"
                              >
                                <ChevronDown 
                                  className={`w-5 h-5 transform transition-transform ${
                                    mobileServicesOpen ? 'rotate-180' : ''
                                  }`} 
                                />
                              </button>
                            </div>
                            
                            <AnimatePresence>
                              {mobileServicesOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="pl-4 space-y-3 border-l-2 border-[#4b9d96] ml-2 mt-2"
                                >
                                  {servicesLinks[0].links.map((link) => (
                                    <div key={link.name}>
                                      {link.subItems ? (
                                        <div className="space-y-2">
                                          <div className="font-semibold text-gray-800 text-sm py-2">
                                            {link.name}
                                          </div>
                                          <div className="pl-3 space-y-2 border-l-2 border-gray-200">
                                            {link.subItems.map((subItem) => (
                                              <button
                                                key={subItem.name}
                                                onClick={() => handleSectionNavigation(subItem.href)}
                                                className="block w-full text-left py-2 text-gray-600 hover:text-[#4b9d96] transition-colors duration-300 text-sm"
                                              >
                                                {subItem.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => handleSectionNavigation(link.href)}
                                          className="block w-full text-left py-2 text-gray-600 hover:text-[#4b9d96] transition-colors duration-300 text-sm font-medium"
                                        >
                                          {link.name}
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}

                        {/* Produits Mobile */}
                        {item.name === 'Produits' && (
                          <div>
                            <div className="flex items-center justify-between">
                              <Link
                                to={item.href}
                                className="text-lg font-semibold text-gray-900 hover:text-[#4b9d96] transition-colors"
                                onClick={() => setIsOpen(false)}
                              >
                                Produits
                              </Link>
                              <button
                                onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                                className="p-2 text-gray-500 hover:text-[#4b9d96]"
                              >
                                <ChevronDown 
                                  className={`w-5 h-5 transform transition-transform ${
                                    mobileProductsOpen ? 'rotate-180' : ''
                                  }`} 
                                />
                              </button>
                            </div>
                            
                            <AnimatePresence>
                              {mobileProductsOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="pl-4 space-y-3 border-l-2 border-[#4b9d96] ml-2 mt-2"
                                >
                                  {productsLinks[0].links.map((link) => (
                                    <div key={link.name}>
                                      {link.subItems ? (
                                        <div className="space-y-2">
                                          <div className="font-semibold text-gray-800 text-sm py-2">
                                            {link.name}
                                          </div>
                                          <div className="pl-3 space-y-2 border-l-2 border-gray-200">
                                            {link.subItems.map((subItem) => (
                                              <button
                                                key={subItem.name}
                                                onClick={() => handleSectionNavigation(subItem.href)}
                                                className="block w-full text-left py-2 text-gray-600 hover:text-[#4b9d96] transition-colors duration-300 text-sm"
                                              >
                                                {subItem.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      ) : (
                                        <button
                                          onClick={() => handleSectionNavigation(link.href)}
                                          className="block w-full text-left py-2 text-gray-600 hover:text-[#4b9d96] transition-colors duration-300 text-sm font-medium"
                                        >
                                          {link.name}
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`block py-3 text-lg transition-colors duration-300 ${
                          isActive(item.href)
                            ? 'text-[#4b9d96] font-semibold'
                            : 'text-gray-700 hover:text-[#4b9d96]'
                        }`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Bouton Contact Mobile - Très visible */}
                <div className="pt-4 border-t border-gray-200">
                  <Link
                    to="/contact"
                    className="block w-full text-center bg-gradient-to-r from-[#4B9D96] to-[#cd3127] text-white py-4 rounded-xl hover:shadow-lg transition-all duration-300 font-bold text-lg"
                    onClick={() => setIsOpen(false)}
                  >
                    Contactez-nous
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
